import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import CreateSupermanMemberForm from '../components/CreateSupermanMemberForm';
import EditSupermanMemberForm from '../components/EditSupermanMemberForm';
import SupermanMemberDetailsModal from '../components/SupermanMemberDetailsModal';

const SupermanMemberPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
    const viewModalFetchedIdRef = useRef(null);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchItems = async (page = 1, search = '') => {
        try {
            setLoading(true);
            let url = `${process.env.REACT_APP_API_URL}/api/supermanmembers?page=${page}&limit=20&sort=-createdAt`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            const res = await axios.get(url, { headers: authHeaders() });
            if (res.data.success) {
                setItems(res.data.data);
                setTotal(res.data.pagination.total);
                setTotalPages(res.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch Superman Members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateForm(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!showViewModal || !selected?._id) return;
        const id = selected._id;
        if (viewModalFetchedIdRef.current === id) return;
        viewModalFetchedIdRef.current = id;

        setViewDetailsLoading(true);
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/supermanmembers/${id}`, { headers: authHeaders() })
            .then((res) => {
                if (res.data.success) setSelected(res.data.data);
            })
            .catch((err) => {
                setError(`Failed to fetch details: ${err.response?.data?.error || err.message}`);
            })
            .finally(() => setViewDetailsLoading(false));
    }, [showViewModal, selected?._id]);

    const handleCreateSuccess = () => {
        setShowCreateForm(false);
        fetchItems(currentPage, searchTerm);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        setSelected(null);
        fetchItems(currentPage, searchTerm);
    };

    const handleView = (row) => {
        setSelected(row);
        setShowViewModal(true);
        setViewDetailsLoading(true);
        viewModalFetchedIdRef.current = null;
    };

    const handleEdit = async (row) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/supermanmembers/${row._id}`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setSelected(res.data.data);
                setShowEditModal(true);
            }
        } catch (err) {
            setError(`Failed to fetch for edit: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        try {
            setDeleteLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/supermanmembers/${selected._id}`,
                { headers: authHeaders() }
            );
            setShowDelete(false);
            setSelected(null);
            fetchItems(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Superman Members</h1>
                        <p className="text-gray-600">Total: {total}</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create Superman Member
                    </button>
                </div>

                <div className="mb-6 flex gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold"
                            placeholder="Search name, webID, nationality, contacts..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (setSearchTerm(searchInput), setCurrentPage(1))}
                        />
                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <button
                        onClick={() => { setSearchTerm(searchInput); setCurrentPage(1); }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Search
                    </button>
                    <button
                        onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Clear
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Web ID</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nationality</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Setup</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center">No Superman Members found</td></tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm">{row.name || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.webID || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.nationality || '—'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {row.openHour || '—'} – {row.closeHour || '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{row.newSetupDone ? '✅' : '❌'}</td>
                                        <td className="px-4 py-3 text-right space-x-1">
                                            <button
                                                onClick={() => handleView(row)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full"
                                                title="View"
                                            >
                                                <FiEye />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(row)}
                                                className="p-1.5 text-green-600 hover:bg-green-100 rounded-full"
                                                title="Edit"
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                onClick={() => { setSelected(row); setShowDelete(true); }}
                                                className="p-1.5 text-red-600 hover:bg-red-100 rounded-full"
                                                title="Delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                {showViewModal && selected && (
                    <SupermanMemberDetailsModal
                        supermanMember={selected}
                        detailsLoading={viewDetailsLoading}
                        onClose={() => {
                            setShowViewModal(false);
                            setSelected(null);
                            setViewDetailsLoading(false);
                            viewModalFetchedIdRef.current = null;
                        }}
                        onUpdated={() => fetchItems(currentPage, searchTerm)}
                    />
                )}

                {showDelete && selected && (
                    <DeleteConfirmationModal
                        title="Delete Superman Member"
                        message="Delete this Superman Member?"
                        itemName={selected.name || selected.webID || selected._id}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => { setShowDelete(false); setSelected(null); }}
                    />
                )}

                {showEditModal && selected && (
                    <EditSupermanMemberForm
                        supermanMember={selected}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelected(null);
                        }}
                    />
                )}

                {showCreateForm && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-8 pb-12">
                        <div className="relative max-w-5xl w-full mx-4 bg-amber-50 rounded-lg shadow-lg p-6">
                            <CreateSupermanMemberForm
                                onSuccess={handleCreateSuccess}
                                onCancel={() => setShowCreateForm(false)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SupermanMemberPage;
